import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useGo, useCustomMutation, useCustom, useApiUrl, useList, useDelete } from '@refinedev/core';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormHeader } from '@/components/forms';
import { ChevronDown, ChevronRight, User, Calendar, Search, UserCheck, Plus, Tag, Kanban, Table2, Eye, Trash2, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getLeadDisplayName, type Lead, type LeadFunnel, type LeadStatusConfig, type AdminUser } from './types';
import type { TagItem } from '../tags/types';
import { LeadFunnelStageFields } from './funnels/form';
import { LeadStageAnalyticsDialog } from './LeadStageAnalyticsDialog';
import { notify } from '@/lib/notify';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { useConfirm } from '@/hooks/use-confirm';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

const KANBAN_PAGE_SIZE = 20;

interface LeadKanbanColumnPage {
  items: Lead[];
  total: number;
  hasMore: boolean;
}

interface LeadFilterValues {
  search?: string;
  funnelId?: number;
  assignedAdminId?: number;
  tagId?: number;
}

function parsePositiveInteger(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parseUrlFilters(params: URLSearchParams): LeadFilterValues {
  const result: LeadFilterValues = {};
  const search = params.get('search');
  if (search) { result.search = search; }
  const funnelId = parsePositiveInteger(params.get('funnelId'));
  if (funnelId !== undefined) { result.funnelId = funnelId; }
  const assignedAdminId = parsePositiveInteger(params.get('assignedAdminId'));
  if (assignedAdminId !== undefined) { result.assignedAdminId = assignedAdminId; }
  const tagId = parsePositiveInteger(params.get('tagId'));
  if (tagId !== undefined) { result.tagId = tagId; }
  return result;
}

function filtersToUrlParams(values: LeadFilterValues): Record<string, string> {
  const result: Record<string, string> = {};
  if (values.search) { result.search = values.search; }
  if (values.funnelId !== undefined) { result.funnelId = String(values.funnelId); }
  if (values.assignedAdminId !== undefined) { result.assignedAdminId = String(values.assignedAdminId); }
  if (values.tagId !== undefined) { result.tagId = String(values.tagId); }
  return result;
}

function filtersToQuery(values: LeadFilterValues): Record<string, string> {
  const result: Record<string, string> = {};
  if (values.search) { result.search = values.search; }
  if (values.assignedAdminId !== undefined) { result.assignedAdminId = String(values.assignedAdminId); }
  return result;
}

function getDndLead(data: Record<string, unknown> | undefined): Lead | undefined {
  if (!data || typeof data !== 'object' || !('lead' in data)) { return undefined; }
  const candidate = data.lead;
  if (
    typeof candidate === 'object'
    && candidate !== null
    && 'id' in candidate
    && 'statusConfigId' in candidate
  ) {
    return candidate as Lead;
  }
  return undefined;
}

function formatRelativeDate(dateString: string): string {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) { return 'только что'; }
  if (diffMin < 60) { return `${diffMin}м назад`; }
  if (diffHours < 24) { return `${diffHours}ч назад`; }
  if (diffDays < 7) { return `${diffDays}д назад`; }

  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

// --- Lead Card ---

interface LeadCardProps {
  lead: Lead;
  onClick: () => void;
  isDragOverlay?: boolean;
}

function LeadCard({ lead, onClick, isDragOverlay }: LeadCardProps) {
  const displayName = getLeadDisplayName(lead);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `lead-${lead.id}`,
    data: { lead },
    disabled: isDragOverlay,
  });

  return (
    <Card
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'cursor-grab active:cursor-grabbing py-2.5 px-3 gap-1.5 hover:shadow-md transition-shadow select-none touch-none',
        isDragging && 'opacity-30',
        isDragOverlay && 'shadow-xl ring-2 ring-primary/30 rotate-2',
      )}
      onClick={(e) => {
        if (isDragOverlay) { return; }
        e.stopPropagation();
        onClick();
      }}
    >
      {/* Row 1: ID + date */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-muted-foreground">#{lead.id}</span>
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatRelativeDate(lead.createdAt)}
        </span>
      </div>

      {/* Row 2: Name */}
      <div className="flex items-center gap-1.5">
        <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <p className="text-sm font-medium truncate">{displayName}</p>
        {lead.user && (
          <UserCheck className="h-3 w-3 text-emerald-500 flex-shrink-0" aria-label="Привязан к пользователю" />
        )}
      </div>

      {/* Row 3: Contact info */}
      {(lead.email || lead.phone || lead.telegramUsername) && (
        <div className="text-[11px] text-muted-foreground truncate">
          {lead.email ?? lead.phone ?? (lead.telegramUsername ? `@${lead.telegramUsername}` : null)}
        </div>
      )}

      {/* Row 4: Tags */}
      {lead.tags && lead.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {lead.tags.slice(0, 3).map((t) => (
            <Badge
              key={t.id}
              variant="outline"
              className="text-[10px] px-1 py-0 font-normal"
              style={{ borderColor: t.tag.color, color: t.tag.color }}
            >
              {t.tag.name}
            </Badge>
          ))}
          {lead.tags.length > 3 && (
            <span className="text-[10px] text-muted-foreground">+{lead.tags.length - 3}</span>
          )}
        </div>
      )}
    </Card>
  );
}

// --- Droppable Kanban Column ---

interface KanbanColumnProps {
  status: LeadStatusConfig;
  kanbanQuery: Record<string, string>;
  onLeadClick: (lead: Lead) => void;
  onStageClick: (status: LeadStatusConfig) => void;
  isOver: boolean;
  apiUrl: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

function KanbanColumn({
  status,
  kanbanQuery,
  onLeadClick,
  onStageClick,
  isOver,
  apiUrl,
  isCollapsed,
  onToggleCollapse,
}: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({
    id: `status-${status.id}`,
    data: { statusId: status.id },
  });

  const infiniteQuery = useInfiniteQuery<LeadKanbanColumnPage>({
    queryKey: ['leads-kanban-column', status.id, kanbanQuery],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = typeof pageParam === 'number' ? pageParam : 1;
      const params = new URLSearchParams({
        ...kanbanQuery,
        statusConfigId: String(status.id),
        page: String(page),
        limit: String(KANBAN_PAGE_SIZE),
      });
      const response = await fetch(`${apiUrl}/leads?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to load leads for status ${status.id}`);
      }
      const body = await response.json() as { data: Lead[]; meta: { total: number } };
      const items = body.data ?? [];
      const total = body.meta?.total ?? 0;
      const loaded = page * KANBAN_PAGE_SIZE;
      return { items, total, hasMore: loaded < total };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) { return; }
      return allPages.length + 1;
    },
    staleTime: 30_000,
    enabled: !isCollapsed,
  });

  const allLeads = useMemo(() => {
    const pages = infiniteQuery.data?.pages ?? [];
    return pages.flatMap((p) => p.items);
  }, [infiniteQuery.data]);

  const totalCount = infiniteQuery.data?.pages[0]?.total ?? 0;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) { return; }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
            void infiniteQuery.fetchNextPage();
          }
        }
      },
      { root: null, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [infiniteQuery]);

  if (isCollapsed) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          'flex flex-col min-w-[44px] w-[44px] flex-shrink-0 h-full rounded-xl bg-muted/40 transition-colors',
          isOver && 'bg-primary/10 ring-2 ring-primary/30 ring-dashed',
        )}
      >
        <button
          className="flex flex-col items-center gap-2 py-3 w-full hover:bg-muted rounded-xl transition-colors"
          onClick={onToggleCollapse}
          title={`Развернуть «${status.name}»`}
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
          <span className="text-xs font-medium [writing-mode:vertical-rl] rotate-180 whitespace-nowrap max-h-[200px] overflow-hidden text-ellipsis">
            {status.name}
          </span>
          <Badge variant="secondary" className="text-[10px] px-1 py-0 font-mono">
            {totalCount}
          </Badge>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col min-w-[280px] w-[280px] flex-shrink-0 h-full rounded-xl transition-colors',
        isOver && 'bg-primary/5 ring-2 ring-primary/20 ring-dashed',
      )}
    >
      <div className="flex items-stretch mb-2 rounded-lg overflow-hidden bg-muted/50">
        <button
          type="button"
          className="flex flex-1 min-w-0 flex-col gap-1.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/70"
          onClick={() => onStageClick(status)}
          title={`Открыть аналитику этапа «${status.name}»`}
        >
          <div className="flex items-center gap-2 w-full">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
            <span className="font-medium text-sm truncate flex-1">{status.name}</span>
            <Badge variant="secondary" className="text-xs px-1.5 py-0 font-mono">
              {infiniteQuery.isLoading ? '…' : totalCount}
            </Badge>
          </div>
        </button>
        <button
          type="button"
          className="flex items-center justify-center w-8 hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors border-l border-border/40"
          onClick={onToggleCollapse}
          title="Свернуть колонку"
          aria-label="Свернуть колонку"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-col gap-1.5 flex-1 px-0.5 overflow-y-auto">
        {infiniteQuery.isLoading ? (
          <>
            <Skeleton className="h-[100px] w-full rounded-xl" />
            <Skeleton className="h-[100px] w-full rounded-xl" />
          </>
        ) : (
          allLeads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onClick={() => onLeadClick(lead)}
            />
          ))
        )}

        {!infiniteQuery.isLoading && allLeads.length === 0 && (
          <div className={cn(
            'text-center py-8 text-sm text-muted-foreground rounded-lg border-2 border-dashed border-transparent transition-colors',
            isOver && 'border-primary/30 bg-primary/5',
          )}>
            {isOver ? 'Перетащите сюда' : 'Нет лидов'}
          </div>
        )}

        {infiniteQuery.hasNextPage && (
          <div ref={sentinelRef} className="py-2 text-center text-xs text-muted-foreground">
            {infiniteQuery.isFetchingNextPage ? 'Загрузка...' : '•'}
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanColumnSkeleton() {
  return (
    <div className="flex flex-col min-w-[280px] w-[280px] flex-shrink-0">
      <Skeleton className="h-10 w-full rounded-lg mb-2" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-[100px] w-full rounded-xl" />
        <Skeleton className="h-[100px] w-full rounded-xl" />
        <Skeleton className="h-[100px] w-full rounded-xl" />
      </div>
    </div>
  );
}

interface LeadsListProps {
  fixedFunnelId?: number;
}

interface LeadCreateFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  telegramUsername: string;
  assignedAdminId: number | null;
}

export function LeadFunnelLeadsPage() {
  const { funnelId } = useParams();
  const parsedFunnelId = parsePositiveInteger(funnelId ?? null);
  return <LeadsList fixedFunnelId={parsedFunnelId} />;
}

// --- Table View Component ---

interface LeadsTableViewProps {
  funnelId?: number;
  admins: AdminUser[];
  tags: TagItem[];
}

function LeadsTableView({ funnelId, admins, tags }: LeadsTableViewProps) {
  const go = useGo();
  const [page, setPage] = usePersistedState('leads:table:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('leads:table:sort', { field: 'createdAt', order: 'desc' });
  const [search, setSearch] = usePersistedState('leads:table:search', '');
  const [assignedAdminId, setAssignedAdminId] = usePersistedState<string>('leads:table:assignedAdminId', 'all');
  const [tagId, setTagId] = usePersistedState<string>('leads:table:tagId', 'all');
  const pageSize = 20;

  const { mutate: deleteLead } = useDelete();
  const { confirm, ConfirmationDialog } = useConfirm();

  const queryFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq' | 'contains'; value: string | number }> = [];
    if (search) {
      result.push({ field: 'search', operator: 'contains', value: search });
    }
    if (funnelId !== undefined) {
      result.push({ field: 'funnelId', operator: 'eq', value: funnelId });
    }
    if (assignedAdminId !== 'all') {
      result.push({ field: 'assignedAdminId', operator: 'eq', value: Number(assignedAdminId) });
    }
    if (tagId !== 'all') {
      result.push({ field: 'tagId', operator: 'eq', value: Number(tagId) });
    }
    return result;
  }, [search, funnelId, assignedAdminId, tagId]);

  const listResult = useList<Lead>({
    resource: 'leads',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const handleDelete = async (items: Lead[]) => {
    if (items.length === 0) { return; }
    const confirmed = await confirm({
      title: items.length === 1
        ? `Удалить лид «${getLeadDisplayName(items[0])}»?`
        : `Удалить ${items.length} лидов?`,
      description: 'Это действие необратимо.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }

    for (const item of items) {
      deleteLead(
        { resource: 'leads', id: item.id },
        {
          onSuccess: () => {
            void listResult.query.refetch();
          },
          onError: (error) => {
            const message = error instanceof Error ? error.message : 'Не удалось удалить лид';
            notify.error(message);
          },
        },
      );
    }
  };

  const columns: Column<Lead>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
    },
    {
      key: 'firstName',
      header: 'Лид',
      sortable: true,
      render: (_, lead) => {
        const name = getLeadDisplayName(lead);
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-sm truncate">{name}</span>
            {lead.email && (
              <span className="text-xs text-muted-foreground truncate">{lead.email}</span>
            )}
            {!lead.email && lead.phone && (
              <span className="text-xs text-muted-foreground">{lead.phone}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'statusConfig',
      header: 'Этап',
      render: (_, lead) => {
        if (!lead.statusConfig) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <div
              className="h-2 w-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: lead.statusConfig.color }}
            />
            <span className="text-sm truncate">{lead.statusConfig.name}</span>
          </div>
        );
      },
    },
    {
      key: 'assignedAdmin',
      header: 'Менеджер',
      render: (_, lead) => (
        lead.assignedAdmin
          ? <span className="text-sm">{lead.assignedAdmin.username}</span>
          : <span className="text-muted-foreground text-sm">—</span>
      ),
    },
    {
      key: 'user',
      header: 'Клиент',
      render: (_, lead) => (
        lead.user
          ? (
            <Badge variant="outline" className="text-xs">
              <UserCheck className="h-3 w-3 mr-1 text-emerald-500" />
              Привязан
            </Badge>
          )
          : <span className="text-muted-foreground text-sm">—</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Создан',
      sortable: true,
      render: (value) => formatRelativeDate(String(value)),
    },
    {
      key: 'updatedAt',
      header: 'Обновлён',
      sortable: true,
      render: (value) => formatRelativeDate(String(value)),
    },
  ];

  const bulkActions: BulkAction<Lead>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/leads/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Сменить этап',
      icon: <ArrowLeftRight className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/leads/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      onClick: (items) => {
        void handleDelete(items);
      },
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по имени, email, телефону..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-8 h-9"
          />
        </div>

        <Select
          value={assignedAdminId}
          onValueChange={(v) => {
            setAssignedAdminId(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue placeholder="Все менеджеры" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все менеджеры</SelectItem>
            {admins.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>{a.username}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={tagId}
          onValueChange={(v) => {
            setTagId(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue placeholder="Все теги" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все теги</SelectItem>
            {tags.map((t) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => go({ to: `/leads/show/${row.id}` })}
        emptyMessage="Нет лидов"
        sort={sort}
        onSortChange={setSort}
        selectable
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}

// --- Main Component ---

function buildLeadCreateInitialState(): LeadCreateFormData {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    telegramUsername: '',
    assignedAdminId: null,
  };
}

export function LeadsList({ fixedFunnelId }: LeadsListProps = {}) {
  useScrollRestoration();
  const go = useGo();
  const queryClient = useQueryClient();
  const apiUrl = useApiUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = usePersistedState<'kanban' | 'table'>('leads:viewMode', 'kanban');

  const [filterValues, setFilterValues] = useState<LeadFilterValues>(() => parseUrlFilters(searchParams));

  useEffect(() => {
    const next = filtersToUrlParams(
      fixedFunnelId === undefined
        ? filterValues
        : { ...filterValues, funnelId: undefined },
    );
    const current: Record<string, string> = {};
    for (const [k, v] of searchParams.entries()) { current[k] = v; }
    if (JSON.stringify(next) === JSON.stringify(current)) { return; }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, fixedFunnelId]);

  const [collapsedColumns, setCollapsedColumns] = useState<Set<number>>(new Set());

  const toggleColumnCollapse = useCallback((statusId: number) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) { next.delete(statusId); } else { next.add(statusId); }
      return next;
    });
  }, []);

  const [activeLeadDnd, setActiveLeadDnd] = useState<Lead | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false);
  const [isCreateStageOpen, setIsCreateStageOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<LeadStatusConfig | null>(null);
  const [leadCreateForm, setLeadCreateForm] = useState<LeadCreateFormData>(() => buildLeadCreateInitialState());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const kanbanQuery = useMemo(() => filtersToQuery(filterValues), [filterValues]);

  const funnelsResult = useCustom<LeadFunnel[]>({
    url: `${apiUrl}/leads/funnels`,
    method: 'get',
  });
  const funnels = useMemo(() => {
    return [...(funnelsResult.query?.data?.data ?? [])].sort((a, b) => a.position - b.position || a.id - b.id);
  }, [funnelsResult.query?.data?.data]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(fixedFunnelId ?? filterValues.funnelId ?? null);

  useEffect(() => {
    if (fixedFunnelId === undefined || selectedFunnelId === fixedFunnelId) {
      return;
    }
    setSelectedFunnelId(fixedFunnelId);
  }, [fixedFunnelId, selectedFunnelId]);

  useEffect(() => {
    if (fixedFunnelId !== undefined) {
      return;
    }
    if (funnels.length === 0) {
      return;
    }
    if (selectedFunnelId !== null && funnels.some((funnel) => funnel.id === selectedFunnelId)) {
      return;
    }
    const fallbackFunnel = funnels.find((funnel) => funnel.isDefault) ?? funnels[0];
    setSelectedFunnelId(fallbackFunnel.id);
    setFilterValues((prev) => ({ ...prev, funnelId: fallbackFunnel.id }));
  }, [fixedFunnelId, funnels, selectedFunnelId]);

  // Load statuses
  const statusesResult = useCustom<LeadStatusConfig[]>({
    url: selectedFunnelId === null
      ? `${apiUrl}/leads/statuses`
      : `${apiUrl}/leads/statuses?leadFunnelId=${selectedFunnelId}`,
    method: 'get',
    queryOptions: {
      enabled: selectedFunnelId !== null,
    },
  });
  const isLoadingStatuses = (funnelsResult.query?.isLoading ?? false) || (statusesResult.query?.isLoading ?? false);

  // Load admins for filter
  const adminsResult = useList<AdminUser>({
    resource: 'admin-users',
    pagination: { mode: 'off' },
  });
  const admins = adminsResult.result?.data ?? [];

  // Load tags for filter
  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { mode: 'off' },
  });
  const tags = tagsResult.result?.data ?? [];

  const sortedStatuses = useMemo(() => {
    return [...(statusesResult.query?.data?.data ?? [])].sort((a, b) => a.position - b.position || a.id - b.id);
  }, [statusesResult.query?.data?.data]);
  const selectedFunnel = funnels.find((funnel) => funnel.id === selectedFunnelId);

  const changeStatusMutation = useCustomMutation();
  const createLeadMutation = useCustomMutation();

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['leads-kanban-column'] });
  }, [queryClient]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const lead = getDndLead(event.active.data.current);
    if (lead) { setActiveLeadDnd(lead); }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverColumnId(event.over?.id?.toString() ?? null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveLeadDnd(null);
    setOverColumnId(null);

    const { active, over } = event;
    if (!over) { return; }

    const lead = getDndLead(active.data.current);
    if (!lead) { return; }

    const overId = over.id.toString();
    const targetStatusId = overId.startsWith('status-') ? parseInt(overId.replace('status-', ''), 10) : null;
    if (!targetStatusId || targetStatusId === lead.statusConfigId) { return; }

    changeStatusMutation.mutate(
      {
        url: `/leads/${lead.id}/change-status`,
        method: 'post',
        values: { statusConfigId: targetStatusId },
      },
      { onSuccess: invalidateAll },
    );
  }, [changeStatusMutation, invalidateAll]);

  const handleDragCancel = useCallback(() => {
    setActiveLeadDnd(null);
    setOverColumnId(null);
  }, []);

  const handleLeadClick = useCallback((lead: Lead) => {
    go({ to: `/leads/show/${lead.id}` });
  }, [go]);

  const handleStageDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedStage(null);
    }
  }, []);

  const handleCreateLead = () => {
    const statusConfig = sortedStatuses.find((status) => status.isInitial) ?? sortedStatuses[0];
    if (!statusConfig) {
      notify.error('Сначала создайте этап воронки');
      return;
    }

    const values = {
      firstName: leadCreateForm.firstName.trim() || undefined,
      lastName: leadCreateForm.lastName.trim() || undefined,
      email: leadCreateForm.email.trim() || undefined,
      phone: leadCreateForm.phone.trim() || undefined,
      telegramUsername: leadCreateForm.telegramUsername.trim() || undefined,
      assignedAdminId: leadCreateForm.assignedAdminId ?? undefined,
      statusConfigId: statusConfig.id,
    };

    const hasContact = values.email || values.phone || values.telegramUsername || values.firstName || values.lastName;
    if (!hasContact) {
      notify.error('Укажите имя, email, телефон или Telegram');
      return;
    }

    createLeadMutation.mutate(
      { url: '/leads', method: 'post', values },
      {
        onSuccess: () => {
          notify.success('Лид создан');
          setIsCreateLeadOpen(false);
          setLeadCreateForm(buildLeadCreateInitialState());
          invalidateAll();
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <FormHeader
        title={fixedFunnelId === undefined ? 'Лиды' : selectedFunnel?.name ?? 'Воронка'}
        description={fixedFunnelId === undefined ? 'Управление лидами' : 'Лиды по этапам воронки'}
        sticky
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {/* View mode toggle */}
            <div className="flex items-center border rounded-md overflow-hidden">
              <Button
                variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-none h-8 px-2.5"
                onClick={() => setViewMode('kanban')}
                title="Канбан"
              >
                <Kanban className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-none h-8 px-2.5"
                onClick={() => setViewMode('table')}
                title="Таблица"
              >
                <Table2 className="h-4 w-4" />
              </Button>
            </div>

            {fixedFunnelId === undefined && (
              <>
                <Button variant="outline" size="sm" onClick={() => go({ to: '/leads/webhooks' })}>
                  <Tag className="h-4 w-4 mr-1" />
                  Webhooks
                </Button>
                <Button variant="outline" size="sm" onClick={() => go({ to: '/leads/funnels' })}>
                  Воронки
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => setIsCreateLeadOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Создать лид
            </Button>
            {fixedFunnelId !== undefined && (
              <Button variant="outline" size="sm" onClick={() => setIsCreateStageOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Создать этап
              </Button>
            )}
          </div>
        }
      />

      <Dialog open={isCreateLeadOpen} onOpenChange={setIsCreateLeadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Создать лид</DialogTitle>
            <DialogDescription>
              Лид попадёт на начальный этап текущей воронки.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lead-create-first-name">Имя</Label>
                <Input
                  id="lead-create-first-name"
                  value={leadCreateForm.firstName}
                  onChange={(event) => setLeadCreateForm((prev) => ({ ...prev, firstName: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-create-last-name">Фамилия</Label>
                <Input
                  id="lead-create-last-name"
                  value={leadCreateForm.lastName}
                  onChange={(event) => setLeadCreateForm((prev) => ({ ...prev, lastName: event.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-create-email">Email</Label>
              <Input
                id="lead-create-email"
                type="email"
                value={leadCreateForm.email}
                onChange={(event) => setLeadCreateForm((prev) => ({ ...prev, email: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lead-create-phone">Телефон</Label>
                <Input
                  id="lead-create-phone"
                  value={leadCreateForm.phone}
                  onChange={(event) => setLeadCreateForm((prev) => ({ ...prev, phone: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-create-telegram">Telegram</Label>
                <Input
                  id="lead-create-telegram"
                  placeholder="@username"
                  value={leadCreateForm.telegramUsername}
                  onChange={(event) => setLeadCreateForm((prev) => ({ ...prev, telegramUsername: event.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Менеджер</Label>
              <Select
                value={leadCreateForm.assignedAdminId === null ? 'none' : String(leadCreateForm.assignedAdminId)}
                onValueChange={(value) => setLeadCreateForm((prev) => ({
                  ...prev,
                  assignedAdminId: value === 'none' ? null : Number(value),
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Не назначен" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Не назначен</SelectItem>
                  {admins.map((admin) => (
                    <SelectItem key={admin.id} value={String(admin.id)}>{admin.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={createLeadMutation.mutation.isPending}
                onClick={() => setIsCreateLeadOpen(false)}
              >
                Отмена
              </Button>
              <Button type="button" disabled={createLeadMutation.mutation.isPending} onClick={handleCreateLead}>
                {createLeadMutation.mutation.isPending ? 'Создание...' : 'Создать'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateStageOpen} onOpenChange={setIsCreateStageOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Создать этап</DialogTitle>
            <DialogDescription>
              Этап станет колонкой текущей воронки.
            </DialogDescription>
          </DialogHeader>
          {selectedFunnelId !== null && (
            <LeadFunnelStageFields
              funnelId={selectedFunnelId}
              position={sortedStatuses.length}
              onCancel={() => setIsCreateStageOpen(false)}
              onSuccess={() => {
                setIsCreateStageOpen(false);
                statusesResult.query?.refetch();
                invalidateAll();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <LeadStageAnalyticsDialog
        open={selectedStage !== null}
        onOpenChange={handleStageDialogOpenChange}
        status={selectedStage}
        kanbanQuery={kanbanQuery}
        onStageSaved={() => {
          statusesResult.query?.refetch();
          invalidateAll();
        }}
      />

      {/* Table view */}
      {viewMode === 'table' && (
        <div className="mt-4 flex-1 overflow-auto">
          <LeadsTableView
            funnelId={selectedFunnelId ?? undefined}
            admins={admins}
            tags={tags}
          />
        </div>
      )}

      {/* Kanban filters — only in kanban mode */}
      {viewMode === 'kanban' && <div className="flex items-center gap-2 mt-4 flex-wrap">
        {fixedFunnelId === undefined && (
          <Select
            value={selectedFunnelId === null ? '' : String(selectedFunnelId)}
            onValueChange={(value) => {
              const nextFunnelId = Number(value);
              setSelectedFunnelId(nextFunnelId);
              setFilterValues((prev) => ({ ...prev, funnelId: nextFunnelId }));
            }}
          >
            <SelectTrigger className="h-9 w-[220px]">
              <SelectValue placeholder="Воронка" />
            </SelectTrigger>
            <SelectContent>
              {funnels.map((funnel) => (
                <SelectItem key={funnel.id} value={String(funnel.id)}>{funnel.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по имени, email, телефону..."
            value={filterValues.search ?? ''}
            onChange={(e) => setFilterValues((prev) => ({ ...prev, search: e.target.value || undefined }))}
            className="pl-8 h-9"
          />
        </div>

        <Select
          value={filterValues.assignedAdminId !== undefined ? String(filterValues.assignedAdminId) : 'all'}
          onValueChange={(v) => setFilterValues((prev) => ({
            ...prev,
            assignedAdminId: v === 'all' ? undefined : Number(v),
          }))}
        >
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue placeholder="Все менеджеры" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все менеджеры</SelectItem>
            {admins.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>{a.username}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterValues.tagId !== undefined ? String(filterValues.tagId) : 'all'}
          onValueChange={(v) => setFilterValues((prev) => ({
            ...prev,
            tagId: v === 'all' ? undefined : Number(v),
          }))}
        >
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue placeholder="Все теги" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все теги</SelectItem>
            {tags.map((t) => (
              <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>}

      {/* Kanban board */}
      {viewMode === 'kanban' && <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <ScrollArea className="w-full flex-1 mt-4">
          <div className={cn('flex gap-4 pb-4 h-full', isLoadingStatuses && 'min-h-[400px]')}>
            {isLoadingStatuses ? (
              <>
                <KanbanColumnSkeleton />
                <KanbanColumnSkeleton />
                <KanbanColumnSkeleton />
              </>
            ) : (
              sortedStatuses.length === 0 ? (
                <div className="flex-1 flex items-center justify-center py-16 text-muted-foreground">
                  <p>Нет статусов лидов</p>
                </div>
              ) : (
                sortedStatuses.map((status) => (
                  <KanbanColumn
                    key={status.id}
                    status={status}
                    kanbanQuery={kanbanQuery}
                    onLeadClick={handleLeadClick}
                    onStageClick={setSelectedStage}
                    isOver={overColumnId === `status-${status.id}`}
                    apiUrl={apiUrl}
                    isCollapsed={collapsedColumns.has(status.id)}
                    onToggleCollapse={() => toggleColumnCollapse(status.id)}
                  />
                ))
              )
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <DragOverlay>
          {activeLeadDnd && (
            <div className="w-[280px]">
              <LeadCard
                lead={activeLeadDnd}
                onClick={() => {}}
                isDragOverlay
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>}
    </div>
  );
}
